// =============================================================================
// Google Calendar Views — List, Week, Day, Month
// =============================================================================

import {
  Calendar, ChevronLeft, ChevronRight, MapPin, Clock, Building2,
  ExternalLink, Trash2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

// ── Helpers (partagés) ──────────────────────────────────────────────────────

function getEventInstallateur(event) {
  if (!event) return "Interne";
  if (event.installateur) return event.installateur;
  const attendees = event.attendees || [];
  if (attendees.length > 0) {
    const ext = attendees.find(a => !a.self);
    if (ext) return ext.displayName || ext.name || ext.email?.split("@")[0] || "Installateur";
  }
  return "Interne";
}

function getEventVisiteur(event) {
  if (!event) return "";
  if (event.visiteur) return event.visiteur;
  return "";
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}

function formatDateRange(start, end) {
  if (!start) return "";
  const hasTime = start.includes("T");
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : startDate;

  if (hasTime) {
    const startTime = startDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    const endTime = endDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    if (startDate.toDateString() === endDate.toDateString()) {
      return `${startTime} - ${endTime}`;
    }
    return `${formatDate(start)} ${startTime} → ${formatDate(end)} ${endTime}`;
  }

  if (startDate.toDateString() === endDate.toDateString()) {
    return "Journée entière";
  }
  return `${formatDate(start)} → ${formatDate(end)}`;
}

// ── Vue Liste ───────────────────────────────────────────────────────────────

export function GCalListView({ events, getEventColor, onSelectEvent, onDeleteEvent }) {
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.start?.split("T")[0] || event.start;
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  if (events.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-800/30 rounded-lg" data-testid="gcal-list-empty">
        <Calendar className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
        <p className="text-slate-400 text-lg">Aucun événement ce mois</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gcal-list-view">
      {Object.entries(eventsByDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, dayEvents]) => (
          <div key={date} className="space-y-2">
            <h3 className="text-slate-400 text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(date)}
              <Badge className="bg-slate-700 text-xs">{dayEvents.length}</Badge>
            </h3>

            {dayEvents.map((event) => {
              const colorInfo = getEventColor(event);
              const installateur = getEventInstallateur(event);
              const visiteur = getEventVisiteur(event);

              return (
                <div
                  key={event.id}
                  onClick={() => onSelectEvent(event)}
                  className={`${colorInfo.bg} border-l-4 ${colorInfo.border} rounded-r-lg p-4 cursor-pointer hover:bg-opacity-50 transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`font-medium ${colorInfo.text}`}>{event.title}</span>
                        <Badge className={`${colorInfo.bg} ${colorInfo.text} text-xs`}>
                          {event.is_holiday ? "Jour férié" : installateur}
                        </Badge>
                        {visiteur && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">
                            {visiteur}
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-slate-400">
                        {event.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{formatDateRange(event.start, event.end)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{installateur}</span>
                          {visiteur && <span className="text-emerald-400 ml-2">/ {visiteur}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-4">
                      {event.html_link && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={(e) => { e.stopPropagation(); window.open(event.html_link, "_blank"); }}
                          className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                          title="Ouvrir dans Google Calendar"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      {!event.is_holiday && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={(e) => { e.stopPropagation(); onDeleteEvent(event.id); }}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
}

// ── Vue Semaine ─────────────────────────────────────────────────────────────

export function GCalWeekView({ selectedDate, setSelectedDate, setViewMode, getEventsForDate, getEventColor, onSelectEvent }) {
  const startOfWeek = new Date(selectedDate);
  const dow = startOfWeek.getDay();
  startOfWeek.setDate(startOfWeek.getDate() - (dow === 0 ? 6 : dow - 1));
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + i);
    return d;
  });
  const hours = Array.from({ length: 13 }, (_, i) => i + 7);

  return (
    <div className="bg-slate-800/50 rounded-lg overflow-hidden" data-testid="gcal-week-view">
      {/* Nav semaine */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() - 7); setSelectedDate(d);
        }} className="text-slate-400"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-white text-sm font-medium">
          Semaine du {weekDays[0].toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} au {weekDays[6].toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
        </span>
        <Button variant="ghost" size="sm" onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() + 7); setSelectedDate(d);
        }} className="text-slate-400"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Header jours */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-700">
        <div className="p-2 text-center text-xs text-slate-500"></div>
        {weekDays.map((d, i) => {
          const isToday = d.toDateString() === new Date().toDateString();
          return (
            <div key={i} className={`p-2 text-center border-l border-slate-700/50 ${isToday ? "bg-orange-500/10" : ""}`}
              onClick={() => { setSelectedDate(d); setViewMode("day"); }}
            >
              <div className="text-xs text-slate-500">{["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"][i]}</div>
              <div className={`text-sm font-medium ${isToday ? "text-orange-400" : "text-white"}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* All-day events row */}
      <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-700/50">
        <div className="p-1 text-xs text-slate-600 text-center">J.</div>
        {weekDays.map((d, i) => {
          const allDayEvts = getEventsForDate(d).filter(e => e.all_day);
          return (
            <div key={i} className="p-1 border-l border-slate-700/50 min-h-[24px]">
              {allDayEvts.map(e => {
                const c = getEventColor(e);
                return <div key={e.id} onClick={() => onSelectEvent(e)} className={`${c.bg} ${c.text} text-[10px] px-1 rounded truncate cursor-pointer`} title={e.title}>{e.title}</div>;
              })}
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="max-h-[500px] overflow-y-auto">
        {hours.map(h => (
          <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-700/30">
            <div className="p-1 text-xs text-slate-600 text-right pr-2">{String(h).padStart(2,"0")}:00</div>
            {weekDays.map((d, di) => {
              const dayEvts = getEventsForDate(d).filter(e => {
                if (e.all_day) return false;
                const st = e.start || "";
                if (!st.includes("T")) return false;
                const eventHour = parseInt(st.split("T")[1]?.substring(0, 2), 10);
                return eventHour === h;
              });
              return (
                <div key={di} className="border-l border-slate-700/30 min-h-[40px] p-0.5 relative">
                  {dayEvts.map(e => {
                    const c = getEventColor(e);
                    const timeStr = e.start.split("T")[1]?.substring(0, 5) || "";
                    return (
                      <div key={e.id} onClick={() => onSelectEvent(e)} className={`${c.bg} ${c.text} ${c.border} border-l-2 text-[10px] px-1 py-0.5 rounded cursor-pointer truncate`} title={`${timeStr} ${e.title}`}>
                        <span className="font-semibold">{timeStr}</span> {e.title}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Vue Jour ────────────────────────────────────────────────────────────────

export function GCalDayView({ selectedDate, setSelectedDate, getEventsForDate, getEventColor, onSelectEvent }) {
  const dayEvents = getEventsForDate(selectedDate);
  const hours = Array.from({ length: 14 }, (_, i) => i + 7);
  const allDayEvts = dayEvents.filter(e => e.all_day);
  const timedEvts = dayEvents.filter(e => !e.all_day && e.start?.includes("T"));

  return (
    <div className="bg-slate-800/50 rounded-lg overflow-hidden" data-testid="gcal-day-view">
      {/* Nav jour */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d);
        }} className="text-slate-400"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-white font-medium">
          {selectedDate.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </span>
        <Button variant="ghost" size="sm" onClick={() => {
          const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d);
        }} className="text-slate-400"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* All-day events */}
      {allDayEvts.length > 0 && (
        <div className="p-2 border-b border-slate-700/50">
          <span className="text-xs text-slate-500 mr-2">Journée :</span>
          {allDayEvts.map(e => {
            const c = getEventColor(e);
            return <Badge key={e.id} className={`${c.bg} ${c.text} text-xs mr-1 cursor-pointer`} onClick={() => onSelectEvent(e)}>{e.title}</Badge>;
          })}
        </div>
      )}

      {/* Timeline */}
      <div className="max-h-[550px] overflow-y-auto">
        {hours.map(h => {
          const hourEvts = timedEvts.filter(e => {
            const eventHour = parseInt(e.start.split("T")[1]?.substring(0, 2), 10);
            return eventHour === h;
          });

          return (
            <div key={h} className="flex border-b border-slate-700/30 min-h-[48px]">
              <div className="w-16 shrink-0 p-2 text-right text-xs text-slate-600 border-r border-slate-700/30">
                {String(h).padStart(2,"0")}:00
              </div>
              <div className="flex-1 p-1 space-y-1">
                {hourEvts.map(e => {
                  const c = getEventColor(e);
                  const inst = getEventInstallateur(e);
                  const timeStr = e.start.split("T")[1]?.substring(0, 5) || "";
                  const endTime = e.end?.split("T")[1]?.substring(0, 5) || "";
                  return (
                    <div key={e.id} onClick={() => onSelectEvent(e)}
                      className={`${c.bg} border-l-4 ${c.border} rounded-r p-2 cursor-pointer hover:brightness-110 transition-all`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`${c.text} font-semibold text-sm`}>{timeStr}{endTime ? ` - ${endTime}` : ""}</span>
                          <span className="text-white font-medium text-sm ml-2">{e.title}</span>
                        </div>
                        <Badge className={`${c.bg} ${c.text} text-xs`}>{inst}</Badge>
                      </div>
                      {e.location && (
                        <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                          <MapPin className="w-3 h-3" /> {e.location}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Vue Mois (Calendrier) ───────────────────────────────────────────────────

export function GCalMonthView({ currentMonth, events, getEventsForDate, getEventColor, onSelectEvent, onSelectDate }) {
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    const startWeekday = firstDay.getDay() || 7;
    for (let i = startWeekday - 1; i > 0; i--) {
      const date = new Date(year, month, 1 - i);
      days.push({ date, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const date = new Date(year, month + 1, i);
        days.push({ date, isCurrentMonth: false });
      }
    }

    return days;
  };

  return (
    <div className="bg-slate-800/50 rounded-lg overflow-hidden" data-testid="gcal-month-view">
      {/* En-têtes jours */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 border-b border-slate-700">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
          <div key={day} className="p-2 text-center text-slate-400 text-sm font-medium">
            {day}
          </div>
        ))}
      </div>

      {/* Grille jours */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7">
        {getDaysInMonth().map(({ date, isCurrentMonth }, idx) => {
          const dayEvts = getEventsForDate(date);
          const isToday = date.toDateString() === new Date().toDateString();

          return (
            <div
              key={idx}
              onClick={() => onSelectDate(date)}
              className={`min-h-[100px] border-b border-r border-slate-700/50 p-1 cursor-pointer hover:bg-slate-700/20 ${
                !isCurrentMonth ? "bg-slate-900/50" : ""
              } ${isToday ? "bg-orange-500/10" : ""}`}
            >
              <div className={`text-sm mb-1 ${
                isToday ? "text-orange-400 font-bold" :
                isCurrentMonth ? "text-slate-300" : "text-zinc-400"
              }`}>
                {date.getDate()}
              </div>

              <div className="space-y-1">
                {dayEvts.slice(0, 3).map((event) => {
                  const colorInfo = getEventColor(event);
                  return (
                    <div
                      key={event.id}
                      onClick={(e) => { e.stopPropagation(); onSelectEvent(event); }}
                      className={`${colorInfo.bg} ${colorInfo.text} text-xs px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80`}
                      title={event.title}
                    >
                      {!event.all_day && event.start?.includes("T") && (
                        <span className="font-semibold mr-1">
                          {event.start.split("T")[1]?.substring(0, 5)}
                        </span>
                      )}
                      {event.title}
                    </div>
                  );
                })}
                {dayEvts.length > 3 && (
                  <div className="text-xs text-zinc-400">
                    +{dayEvts.length - 3} autres
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
